/* eslint-disable no-nested-ternary */
/**
 * @name Login
 * @description Login para iniciar sesión en Talentry
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';
import { initReactI18next } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';

// Google ReCAPTCHA
import ReCAPTCHA from 'react-google-recaptcha';

import {
  AuthService,
  EmployeeService,
  InstructorCourseService,
  PasswordService,
  SnackbarService,
  PreviousRouteService
} from '@services';

import { Footer, Lines } from '@components';
import { Environment } from '@config';
import { MUI, Typography } from '@styles';

import LoginForm from './components/login-form';
import ForgotPasswordDialog from './components/forgot-password.dialog';
import TwoFactorForm from './components/two-factor-form';

class Login extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isExternal: false,
      isOpenDialog: false,
      valueRecaptcha: '',
      lockUser: false,
      showPassword: false,
      authChallenge: null,
      pendingLoginData: null,
      challengeTimeTick: Date.now(),
      challengeErrorMessage: '',
      isResendingChallenge: false
    };

    this.values = {
      userName: '',
      password: '',
      keepSession: false,
      rememberDevice: true,
      useRefreshTokens: true
    };

    this.schema = Yup.object({
      userName: Yup.string('').required('*'),
      password: Yup.string('').required('*')
    });

    this.twoFactorValues = {
      code: ''
    };

    this.twoFactorSchema = Yup.object({
      code: Yup.string('')
        .required(i18n.t('Login.twoFactorCodeRequired'))
        .matches(/^[0-9]{6}$/, i18n.t('Login.twoFactorCodeInvalid'))
    });
  }

  componentDidMount() {
    this.addObserver();
  }

  componentWillUnmount() {
    this.clearChallengeTimer();
  }

  handleTogglePassword = () => {
    this.setState((prevState) => ({
      showPassword: !prevState.showPassword
    }));
  };

  resetChallengeState = () => {
    this.clearChallengeTimer();
    this.setState({
      authChallenge: null,
      pendingLoginData: null,
      challengeErrorMessage: '',
      isResendingChallenge: false
    });
  };

  handleRememberDeviceChange = (event) => {
    const rememberDevice = Boolean(event.target.checked);

    this.setState((prevState) => ({
      pendingLoginData: prevState.pendingLoginData
        ? {
          ...prevState.pendingLoginData,
          rememberDevice
        }
        : prevState.pendingLoginData
    }));
  };

  setChallengeState = (challengeData, pendingLoginData = null) => {
    const currentTime = Date.now();
    const expiresInSeconds = Number(challengeData?.expiresInSeconds ?? 0) || 0;
    const resendAvailableInSeconds = Number(challengeData?.resendAvailableInSeconds ?? 0) || 0;

    this.setState({
      authChallenge: {
        ...challengeData,
        expiresAt: currentTime + (expiresInSeconds * 1000),
        resendAvailableAt: currentTime + (resendAvailableInSeconds * 1000)
      },
      pendingLoginData: pendingLoginData ?? this.state.pendingLoginData,
      challengeTimeTick: currentTime,
      challengeErrorMessage: '',
      isResendingChallenge: false
    }, this.startChallengeTimer);
  };

  startChallengeTimer = () => {
    this.clearChallengeTimer();

    this.challengeTimer = window.setInterval(() => {
      const { authChallenge } = this.state;

      if (!authChallenge) {
        this.clearChallengeTimer();
        return;
      }

      const challengeRemaining = this.getRemainingSeconds(authChallenge.expiresAt);
      const resendRemaining = this.getRemainingSeconds(authChallenge.resendAvailableAt);

      this.setState({ challengeTimeTick: Date.now() });

      if (challengeRemaining <= 0 && resendRemaining <= 0) {
        this.clearChallengeTimer();
      }
    }, 1000);
  };

  clearChallengeTimer = () => {
    if (this.challengeTimer) {
      window.clearInterval(this.challengeTimer);
      this.challengeTimer = null;
    }
  };

  getRemainingSeconds = (targetTimestamp) => {
    if (!targetTimestamp) {
      return 0;
    }

    const { challengeTimeTick } = this.state;
    return Math.max(Math.ceil((targetTimestamp - challengeTimeTick) / 1000), 0);
  };

  formatSeconds = (seconds) => {
    const safeSeconds = Math.max(Number(seconds) || 0, 0);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;

    if (minutes > 0) {
      return `${String(minutes).padStart(2, '0')} min ${String(remainingSeconds).padStart(2, '0')} seg`;
    }
    return `${String(remainingSeconds).padStart(2, '0')} seg`;
  };

  getChallengeErrorMessage = (error, fallbackKey = 'Login.twoFactorUnexpectedError') => {
    if (!error) {
      return i18n.t(fallbackKey);
    }

    const responseData = error.response?.data;
    const remainingAttempts = responseData?.RemainingAttempts ?? responseData?.remainingAttempts;

    if (remainingAttempts != null) {
      return i18n.t('Login.twoFactorRemainingAttempts', { count: remainingAttempts });
    }

    if (typeof responseData === 'string') {
      return responseData;
    }

    if (responseData?.message || responseData?.Message) {
      return responseData?.message || responseData?.Message;
    }

    if (responseData?.error_description || responseData?.ErrorDescription) {
      return responseData?.error_description || responseData?.ErrorDescription;
    }

    if (responseData?.error || responseData?.Error) {
      return responseData?.error || responseData?.Error;
    }

    if (error.message && typeof error.message === 'string') {
      return error.message;
    }

    return i18n.t(fallbackKey);
  };

  addObserver = () => {
    // NOTA: Se utiliza MutationObserver para detectar cambios en el atributo del input de tipo password para evitar que la contraseña sea desenmascarada.
    let target = document.getElementById('password');
    if (!target) {
      window.setTimeout(this.addObserver, 500);
      return;
    }

    // Create an observer instance.
    let observer = new MutationObserver((mutation) => {
      if (mutation[0].target.type !== 'password' && !this.state.showPassword) {
        mutation[0].target.type = 'password';
      }
    });

    // Configuration of the observer:
    let config = {
      attributes: true // This is to watch for attribute changes.
    };

    // Pass in the element you wanna watch as well as the options.
    observer.observe(target, config);

    // Later, you can stop observing.
    // observer.disconnect();
  }

  openDialog = () => {
    this.setState({ isOpenDialog: true });
  }

  closeDialog = () => {
    this.setState({ isOpenDialog: false });
  }

  handleReCAPTCHAChange = (value) => {
    AuthService.recaptchaValidation(value)
      .then((response) => {
        if (response.success) {
          this.recaptchaVerifyClean();
          this.setState({ valueRecaptcha: value });
          this.setState({ lockUser: false });
        } else {
          SnackbarService.error('Error, intenta de nuevo');
        }

      }).catch((error) => {
        let errorMsg = (!error) ? i18n.t('HttpResponse.error') : error.response.data;
        SnackbarService.error(errorMsg);
      });
  }

  // Inicia sesión
  login = (data, setSubmitting) => {
    AuthService.interactiveLogin(data)
      .then((response) => {
        if (response?.type === 'challenge') {
          this.recaptchaVerifyClean();
          this.setChallengeState(response.challengeData, data);
          setSubmitting(false);
          return;
        }

        if (response?.type !== 'authenticated') {
          throw new Error('UNEXPECTED_AUTH_RESPONSE');
        }

        return this.handleAuthenticatedLogin(data, setSubmitting);
      })
      .catch((error) => {
        this.resetChallengeState();
        let errorMsg = (!error)
          ? i18n.t('GenericMessages.errorContactAdmin')
          : error.response?.data?.error_description || error.response?.data?.message || error.response?.data || i18n.t('GenericMessages.errorContactAdmin');

        // Si el usuario esta bloqueado, enviar correo para desbloquear
        if (errorMsg === 'Usuario Bloqueado, favor de contactarte con el administrador.') {
          errorMsg = i18n.t('GenericMessages.userBlockedContactAdmin');
          PasswordService.unlock({ Usuario: data.userName });
        }

        this.recaptchaVerify();
        setSubmitting(false);
        SnackbarService.error(errorMsg);
      });
  };

  verifyTwoFactorCode = (values, setSubmitting) => {
    const { authChallenge, pendingLoginData } = this.state;

    if (!authChallenge?.challengeId || !pendingLoginData) {
      setSubmitting(false);
      this.setState({
        challengeErrorMessage: i18n.t('Login.twoFactorUnexpectedError')
      });
      return;
    }

    if (this.getRemainingSeconds(authChallenge.expiresAt) <= 0) {
      setSubmitting(false);
      this.setState({
        challengeErrorMessage: i18n.t('Login.twoFactorExpired')
      });
      return;
    }

    this.setState({ challengeErrorMessage: '' });

    AuthService.verifyTwoFactorChallenge({
      challengeId: authChallenge.challengeId,
      code: values.code,
      rememberDevice: Boolean(pendingLoginData.rememberDevice)
    }, pendingLoginData)
      .then((response) => {
        if (response?.type !== 'authenticated') {
          throw new Error('UNEXPECTED_VERIFY_RESPONSE');
        }

        return this.handleAuthenticatedLogin(pendingLoginData, setSubmitting);
      })
      .catch((error) => {
        setSubmitting(false);
        this.setState({
          challengeErrorMessage: this.getChallengeErrorMessage(error, 'Login.twoFactorVerifyError')
        });
      });
  };

  resendTwoFactorCode = () => {
    const { authChallenge, isResendingChallenge } = this.state;

    if (!authChallenge?.challengeId || isResendingChallenge) {
      return;
    }

    if (this.getRemainingSeconds(authChallenge.expiresAt) <= 0) {
      this.setState({
        challengeErrorMessage: i18n.t('Login.twoFactorExpired')
      });
      return;
    }

    if (this.getRemainingSeconds(authChallenge.resendAvailableAt) > 0) {
      return;
    }

    this.setState({
      isResendingChallenge: true,
      challengeErrorMessage: ''
    });

    AuthService.resendTwoFactorChallenge(authChallenge.challengeId)
      .then((response) => {
        if (response?.type !== 'challenge') {
          throw new Error('UNEXPECTED_RESEND_RESPONSE');
        }

        this.setChallengeState(response.challengeData);
        SnackbarService.success(i18n.t('Login.twoFactorResentSuccess'));
      })
      .catch((error) => {
        this.setState({
          isResendingChallenge: false,
          challengeErrorMessage: this.getChallengeErrorMessage(error, 'Login.twoFactorResendError')
        });
      });
  };

  handleAuthenticatedLogin = (loginData, setSubmitting) => {
    this.clearChallengeTimer();

    const isExternal = new RegExp(/\w+\.Talentry.\w+/).test(loginData.userName);

    this.setState({
      isExternal,
      authChallenge: null,
      pendingLoginData: null,
      challengeErrorMessage: '',
      isResendingChallenge: false
    });

    if (isExternal) {
      return EmployeeService.getInfoExternal(loginData.userName)
        .then((response) => {
          this.recaptchaVerifyClean();
          this.redirectExternalUser(response);
        })
        .catch((error) => {
          setSubmitting(false);
          let errorMsg = (!error) ? i18n.t('HttpResponse.error') : error.response?.data || i18n.t('HttpResponse.error');
          SnackbarService.error(errorMsg);
        });
    }

    return EmployeeService.getInfo()
      .then((response) => {
        localStorage.setItem('sidebarOpen', true);
        this.setUserInfo(response);
        this.recaptchaVerifyClean();
        this.goHome('/home');
      })
      .catch((error) => {
        setSubmitting(false);
        let errorMsg = (!error) ? i18n.t('HttpResponse.error') : error.response?.data || i18n.t('HttpResponse.error');
        SnackbarService.error(errorMsg);
      });
  };

  recaptchaVerify = () => {
    // Obtener el contador
    let recaptchaVerify = JSON.parse(localStorage.getItem('ID'));

    // Si no existe que lo cree
    if (!recaptchaVerify) {
      recaptchaVerify = {
        id: 1
      };
    } else if (recaptchaVerify.id > 5) { // Verificacion si ya va pasar a 8
      this.setState({ lockUser: true });
    } else recaptchaVerify.id++; // al final si no es ninguna de las anteriores que sume

    // Asignar valor    
    localStorage.setItem('ID', JSON.stringify(recaptchaVerify));
  };

  recaptchaVerifyClean = () => {
    // Obtener el contador
    let recaptchaVerify = JSON.parse(localStorage.getItem('ID'));

    if (recaptchaVerify) {
      // Reiniciar valor   
      recaptchaVerify.id = 1;
      localStorage.setItem('ID', JSON.stringify(recaptchaVerify));
    }
  };


  // Guarda información del usuario en LocalStorage
  setUserInfo = (userInfo) => {
    localStorage.setItem('ls.infoUsuario', JSON.stringify(userInfo));
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
    localStorage.setItem('ls.ty-Language', JSON.stringify({ language: userInfo.Empleado.Idioma }));
    sessionStorage.setItem('ls.skipHomeInfoFetchOnce', 'true');

    // Switch Language
    i18n.use(initReactI18next).init({ lng: userInfo.Empleado.Idioma });
  };

  // Redirecciona a Inicio
  goHome = (fallbackPath = '/home', usePreviousRoute = true) => {
    const { history, location } = this.props;
    if (usePreviousRoute) {
      PreviousRouteService.redirectFromLogin(history, location?.state, fallbackPath);
    } else {
      PreviousRouteService.cleanPreviousRoute();
      history.push(fallbackPath);
    }
  };

  // Redirecciona a un usuario externo
  redirectExternalUser = (externalUserInfo) => {
    const { isExternal } = this.state;
    let userInfo = {
      Aplicacion: [],
      Empresa: [],
      Parametro: [],
      Permiso: [],
      Rol: [],
      EsExterno: isExternal,
      Empleado: {
        Idioma: 'es',
        Foto: externalUserInfo.Foto,
        Nombre: externalUserInfo.Nombre
      },
    };

    InstructorCourseService.get()
      .then((response) => {
        if (response.length > 0) {
          userInfo.Aplicacion.push({
            IdAplicacion: 10,
            Aplicacion: 'Academy',
            Descripcion: '',
            Logo: 'https://assets.talentry.io/icons/appicons/academy.png',
            Estatus: true
          });
          this.setUserInfo(userInfo);
          let URL = Environment.getCurrent().domain;
          window.location.href = `${URL.substring(0, URL.length - 2)}academy/#!/trainer-course`;
          return;
        }

        this.setUserInfo(userInfo);
        this.goHome('/bienvenida/externo', false);
      })
      .catch(() => {
        this.setUserInfo(userInfo);
        this.goHome('/bienvenida/externo', false);
      });
  }

  // Modal de 'Recuperar contraseña'
  forgotPassword = () => {
    this.setState({ isOpenDialog: true });
  };

  renderChallengeForm = () => {
    const {
      authChallenge,
      pendingLoginData,
      challengeErrorMessage,
      isResendingChallenge
    } = this.state;

    if (!authChallenge) {
      return null;
    }

    const expiresInSeconds = this.getRemainingSeconds(authChallenge.expiresAt);
    const resendAvailableInSeconds = this.getRemainingSeconds(authChallenge.resendAvailableAt);
    const isExpired = expiresInSeconds <= 0;

    return (
      <Formik
        key={`${authChallenge.challengeId}-${authChallenge.expiresAt}`}
        validateOnMount
        validationSchema={this.twoFactorSchema}
        initialValues={this.twoFactorValues}
        onSubmit={(values, { setSubmitting }) => {
          this.verifyTwoFactorCode(values, setSubmitting);
        }}
      >
        {(formikProps) => (
          <TwoFactorForm
            {...formikProps}
            maskedEmail={authChallenge.maskedEmail}
            expiresInText={this.formatSeconds(expiresInSeconds)}
            resendInText={this.formatSeconds(resendAvailableInSeconds)}
            challengeErrorMessage={challengeErrorMessage}
            canResend={resendAvailableInSeconds <= 0}
            isExpired={isExpired}
            isResending={isResendingChallenge}
            onResend={this.resendTwoFactorCode}
            rememberDevice={Boolean(pendingLoginData?.rememberDevice)}
            onRememberDeviceChange={this.handleRememberDeviceChange}
          />
        )}
      </Formik>
    );
  };

  render() {
    const { authChallenge, lockUser } = this.state;
    const isChallengeActive = Boolean(authChallenge?.requiresTwoFactor && authChallenge?.challengeId);
    const formSectionHeights = isChallengeActive
      ? {
        title: '15%',
        content: '75%',
        actions: '10%'
      }
      : {
        title: '20%',
        content: '60%',
        actions: '20%'
      };

    // Set Language Default (ES)
    if (!JSON.parse(localStorage.getItem('ls.ty-Language'))) {
      localStorage.setItem('ls.ty-Language', JSON.stringify({ language: 'es' }));
    }

    return (
      <>
        <Styled.LoginWrapper>
          <Styled.TopLines>
            <Lines
              direction="horizontal"
              firstLine={{ size: 75, color: 'blueMedium' }}
              secondLine={{ size: 25, color: 'red' }}
            />
          </Styled.TopLines>

          <Styled.FlexContent>
            <MUI.Grid container>
              <MUI.Grid item sm={6} xs={12}>
                <Styled.FlexRow>
                  <Styled.FlexLogo>
                    <Styled.LogoTalentry
                      src="https://assets.talentry.io/logo/logo-talentry.png"
                      alt="Talentry"
                    />
                  </Styled.FlexLogo>
                  <Styled.Divider orientation="vertical" />
                </Styled.FlexRow>
              </MUI.Grid>

              <MUI.Grid item sm={6} xs={12}>
                <Styled.FlexForm className={isChallengeActive ? 'challenge-active' : ''}>
                  <MUI.Box display="flex" alignItems="center" height={formSectionHeights.title}>
                    <Typography.H4>
                      {!lockUser && (isChallengeActive ? i18n.t('Login.twoFactorTitle') : i18n.t('Login.sessionStart'))}
                    </Typography.H4>
                  </MUI.Box>
                  <MUI.Box display="flex" alignItems="center" height={formSectionHeights.content}>
                    {lockUser ? (
                      <MUI.Box display="flex" alignItems="center" width="100%" justifyContent="center">
                        <ReCAPTCHA
                          sitekey="6Le8XbkpAAAAANzcS23YOAyuzIyXymZ0WAYbQUbN"
                          onChange={this.handleReCAPTCHAChange}
                        />
                      </MUI.Box>
                    ) : isChallengeActive ? (
                      this.renderChallengeForm()
                    ) : (
                      <Formik
                        validateOnMount
                        validationSchema={this.schema}
                        initialValues={this.values}
                        initialErrors={this.values}
                        initialTouched={this.values}
                        onSubmit={(values, { setSubmitting }) => {
                          this.login(values, setSubmitting);
                        }}
                      >
                        {(formikProps) => (
                          <LoginForm
                            {...formikProps}
                            showPassword={this.state.showPassword}
                            handleTogglePassword={this.handleTogglePassword}
                          />
                        )}
                      </Formik>
                    )}
                  </MUI.Box>
                  <MUI.Box display="flex" alignItems="center" justifyContent="center" height={formSectionHeights.actions}>
                    {isChallengeActive ? (
                      <MUI.ButtonLink onClick={this.resetChallengeState}>
                        {i18n.t('Login.backToCredentials')}
                      </MUI.ButtonLink>
                    ) : (
                      <MUI.ButtonLink onClick={this.forgotPassword}>
                        {!lockUser && i18n.t('Login.forgotPassword')}
                      </MUI.ButtonLink>
                    )}
                  </MUI.Box>
                </Styled.FlexForm>
              </MUI.Grid>
            </MUI.Grid>
          </Styled.FlexContent>

          <Footer />

          {/* Forgot Password Dialog */}
          {this.state.isOpenDialog ? (
            <ForgotPasswordDialog
              open={this.state.isOpenDialog}
              onClose={this.closeDialog}
            />
          ) : null}
        </Styled.LoginWrapper>
      </>
    );
  }
}

Login.propTypes = {
  // History router.
  history: PropTypes.object,
  location: PropTypes.object
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.LoginWrapper = styled.div`
  display: flex;
  flex-flow: column;
  height: 100%;
`;

Styled.FlexContent = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  padding: 16px;
  margin-bottom: 64px;

  @media (max-width: 600px) {
    margin-bottom: 0px;
  }
`;

Styled.FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
`;

Styled.FlexLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 48px 72px;
  margin-left: 100px;
  margin-right: 8px;

  @media (max-width: 940px) {
    padding: 48px;
    margin: 0px;
  }
`;

Styled.FlexForm = styled.div`
  display: flex;
  flex-direction: column;
  height: 450px;
  padding: 48px 72px;
  margin-left: 8px;
  margin-right: 100px;

  &.challenge-active {
    padding: 0px 50px;
  }

  @media (max-width: 940px) {
    padding: 48px;
    margin: 0px;

    &.challenge-active {
      padding: 0px 50px;
    }
  }
`;

Styled.TopLines = styled.div`
  padding-top: 60px;
`;

Styled.LogoTalentry = styled.img`
  width: 100%;
`;

Styled.Divider = styled(MUI.Divider)`
  height: initial;

  @media (max-width: 600px) {
    display: none;
  }
`;

export default Login;
